import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Chess } from 'chess.js';
import type { Key } from 'chessground/types';
import type { DrawShape } from 'chessground/draw';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faRotateRight,
  faArrowRight,
  faArrowDown,
  faXmark,
  faLightbulb,
  faEye,
  faHandPointer,
  faChessKnight,
} from '@fortawesome/free-solid-svg-icons';
import AppContext from '../context/AppContext';
import GameContext from '../context/GameContext';
import { useAppDispatch } from '../store';
import { openPlayOut } from '../reducers/uiReducer';
import { analytics } from '../utils/analytics';
import { MoveType } from '../types/MoveType';
import { getIcon, moveTypeClass } from '../utils/ChessTypes';
import { formatMoney, previewSuccessCents } from '../utils/earnings';
import { usePractice } from './PracticeProvider';
import { useCorrections } from './CorrectionsProvider';
import {
  buildForward,
  isGoodOrBetter,
  moveRoom,
  roomHintText,
  solutionPiece,
  uciToSan,
} from './correctionGrading';

// The arrow brush for a mistake severity (registered on the Chessviewer's chessground).
function severityBrush(mt: MoveType | null | undefined): string {
  switch (mt) {
    case MoveType.Blunder:
      return 'scBlunder';
    case MoveType.Mistake:
      return 'scMistake';
    case MoveType.Miss:
      return 'scMiss';
    case MoveType.Inaccuracy:
      return 'scInaccuracy';
    default:
      return 'yellow';
  }
}

// The prompt and result below the game board while practising: "Black to move. You made a blunder"
// before the move, then the reveal (your move classified, Try again / Next, the best move). Reuses the
// standalone correction board's cm-correct-* styles.
export default function PracticeBelowBoard(): React.ReactNode {
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const { chessviewerRef, game } = useContext(GameContext);
  const { active, selected, outcome, stop, retry, hasNextRepair, advance, singleMode } =
    usePractice();
  // The hint flag lives in CorrectionsProvider, where the payout is applied.
  const { markHintUsed } = useCorrections();
  const dispatch = useAppDispatch();

  // Play the mistake's position out against the engine, from the solve position, as in the repair
  // session.
  const playItOut = () => {
    if (selected == null) return;
    const opponent = selected.SideToMove === 'white' ? game.Black : game.White;
    analytics.track('play_out_start', { ui_source: 'game' });
    dispatch(
      openPlayOut({
        fen: selected.Fen,
        color: selected.SideToMove,
        evaluationCp: selected.Evaluation,
        label: `${opponent ? `vs ${opponent} · ` : ''}move ${selected.Number}`,
      }),
    );
  };

  // The engine's best continuation after the move just played, as clickable chips that walk the board
  // through it. lineCursor is the ply the board is parked on (null = your move).
  const forward = useMemo(
    () =>
      outcome?.afterFen && outcome.bestLineUci && outcome.bestLineUci.length > 0
        ? buildForward(outcome.afterFen, outcome.bestLineUci)
        : { positions: [], plies: [] },
    [outcome?.afterFen, outcome?.bestLineUci],
  );
  const [lineCursor, setLineCursor] = useState<number | null>(null);
  // Help asked for on this attempt, as in the repair session: the move played (free) and the engine's
  // piece (halves the payout). Reset per move and on retry.
  const [showMistake, setShowMistake] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  // Reset reveal state when the move or its outcome changes. The provider's hint flag is cleared only
  // once the reveal is gone, so it's still set while the attempt that used it is graded.
  useEffect(() => {
    setLineCursor(null);
    setShowMistake(false);
    setHintUsed(false);
    if (outcome == null && selected != null) markHintUsed(selected.MoveId, false);
    // markHintUsed is stable (useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.MoveId, outcome != null]);

  // The from/to of the game's mistake move, for the hint arrow.
  const playedArrow = useMemo(() => {
    if (selected == null) return null;
    try {
      const c = new Chess(selected.Fen);
      const mv = c.move(selected.PlayedMove);
      return mv ? { from: mv.from as Key, to: mv.to as Key } : null;
    } catch {
      return null;
    }
  }, [selected]);

  // What the panel can say about the position: how much room it gave (free) and which piece the engine
  // moves (the paid hint). Either can be missing, and then it isn't offered.
  const room = useMemo(
    () =>
      selected == null
        ? null
        : moveRoom(selected.Fen, selected.GoodMoveCount, selected.LegalMoveCount),
    [selected],
  );
  const pieceHint = useMemo(
    () => (selected == null ? null : solutionPiece(selected.Fen, selected.Solution)),
    [selected],
  );
  // Draw what's been asked for: the mistake arrow and the engine's piece ring, on the same shapes layer
  // as the result arrow. Flags are passed in because this runs before the state update lands.
  const drawHelp = (mistake: boolean, piece: boolean) => {
    const viewer = chessviewerRef.current;
    if (viewer == null) return;
    const shapes: DrawShape[] = [];
    if (mistake && playedArrow != null) {
      shapes.push({
        orig: playedArrow.from,
        dest: playedArrow.to,
        brush: severityBrush(selected?.MoveType),
      });
    }
    if (piece && pieceHint != null) {
      shapes.push({ orig: pieceHint.square, brush: 'hintPiece' });
    }
    viewer.cg.setShapes(shapes);
  };

  // The paid one: ring the engine's piece and tell the provider, which prices the payout.
  const takeHint = () => {
    if (selected == null) return;
    setHintUsed(true);
    markHintUsed(selected.MoveId, true);
    drawHelp(showMistake, true);
  };

  if (!active || selected == null) return null;

  // Before the move: whose turn and what they played, plus the two help offers.
  if (outcome == null) {
    return (
      <div className="cm-practice-await">
        {/* Hand cue · "Black to move. You made a miss" · the two help buttons, on one row. */}
        <div className="cm-practice-cue-row">
          <FontAwesomeIcon icon={faHandPointer} className="cm-practice-cue-icon" />
          <MadeMistakeLine
            sideToMove={selected.SideToMove}
            moveType={selected.MoveType}
            swing={selected.PlayedSwing}
            payoutCents={previewSuccessCents(
              selected.PlayedSwing,
              selected.effectivePayoutPriors,
              hintUsed,
            )}
            hintUsed={hintUsed}
          />
          {!showMistake ? (
            <button
              type="button"
              className="cm-practice-hint-btn"
              onClick={() => {
                setShowMistake(true);
                drawHelp(true, hintUsed);
              }}
            >
              <FontAwesomeIcon icon={faEye} className="me-1" />
              Show my mistake
            </button>
          ) : null}
          {pieceHint != null && !hintUsed ? (
            <button
              type="button"
              className="cm-practice-hint-btn is-costly"
              onClick={takeHint}
              title="Shows which piece the engine moves — halves what this repair pays"
            >
              <FontAwesomeIcon icon={faLightbulb} className="me-1" />
              Hint
              <span className="cm-practice-hint-cost">half pay</span>
            </button>
          ) : null}
        </div>
        {/* Free help (room in the position) plus whatever was asked for, each line kept. */}
        {showMistake || room != null || hintUsed ? (
          <div className="cm-practice-hint-text">
            {showMistake ? (
              <div className="cm-practice-hint-step">
                You played <strong className="cm-mono">{selected.PlayedMove}</strong> here, find a
                better move.
              </div>
            ) : null}
            {room != null ? (
              <div className="cm-practice-hint-step">{roomHintText(room)}</div>
            ) : null}
            {hintUsed && pieceHint != null ? (
              <div className="cm-practice-hint-step">
                The engine moves the {pieceHint.name} on{' '}
                <strong className="cm-mono">{pieceHint.square}</strong>, ringed in blue.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const playedSameMove = outcome.playedSan === selected.PlayedMove;

  // Only when the engine graded the move and there's a line to walk.
  const bestLine =
    outcome.afterFen != null && outcome.bestLineUci != null && forward.plies.length > 0
      ? { afterFen: outcome.afterFen, uci: outcome.bestLineUci }
      : null;

  return (
    <div className={`cm-correct-result ${outcome.correct ? 'is-correct' : 'is-wrong'}`}>
      {/* Your move classified, with "Correct." inline for a good move. */}
      <div className="cm-correct-head-row">
        <div className="cm-cp-move-head cm-correct-eval">
          {outcome.moveType != null ? <MoveTypeReadout moveType={outcome.moveType} /> : null}
          {/* Only when win chance was actually lost. */}
          {outcome.dropPct != null &&
          outcome.dropPct > 0 &&
          (outcome.moveType == null || !isGoodOrBetter(outcome.moveType)) ? (
            <span
              className="cm-cp-move-swing down"
              title={`You gave up ${outcome.dropPct}% win chance`}
            >
              <FontAwesomeIcon icon={faArrowDown} className="me-1" />
              {outcome.dropPct}%
            </span>
          ) : null}
          {outcome.correct ? (
            <span className="cm-correct-result-head">
              <FontAwesomeIcon icon={faCheck} className="me-2" />
              {outcome.bestMoveSan != null && outcome.playedSan === outcome.bestMoveSan
                ? 'Correct. The engine’s move.'
                : 'That works. A good move or better.'}
            </span>
          ) : null}
          {playedSameMove ? (
            <span className="cm-correct-exact-note">this is the exact move you made</span>
          ) : null}
        </div>

        <div className="cm-correct-actions">
          {/* Finding the move and still winning the position are different things. */}
          <Button
            variant="outline-secondary"
            className="cm-correct-btn cm-playout-launch"
            onClick={playItOut}
            title="Play this position out against the engine"
          >
            <FontAwesomeIcon icon={faChessKnight} className="me-2" />
            Play it out
          </Button>
          <Button
            variant={outcome.correct ? 'outline-secondary' : 'primary'}
            className="cm-correct-btn"
            onClick={retry}
          >
            <FontAwesomeIcon icon={faRotateRight} className="me-2" />
            Try again
          </Button>
          {/* Next move to repair, flowing into the next tier. "Done" only when nothing is left (or in
              single-move repair). */}
          {!singleMode && hasNextRepair ? (
            <Button
              variant={outcome.correct ? 'primary' : 'outline-secondary'}
              className="cm-correct-btn"
              onClick={advance}
            >
              Next
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          ) : (
            <Button variant="success" className="cm-correct-btn" onClick={stop}>
              Done
            </Button>
          )}
        </div>
      </div>

      {/* The engine's best line from your position; click to play it out, Exit to return. */}
      {bestLine != null ? (
        <div className="cm-correct-bestline cm-cp-bestline">
          <span className="cm-cp-bestline-label">From here:</span>
          {forward.plies.map((ply, i) => (
            <React.Fragment key={i}>
              {ply.numberLabel ? (
                <span className="cm-cp-bestline-num cm-mono">{ply.numberLabel}</span>
              ) : null}
              <button
                type="button"
                className={`cm-cp-bestline-move cm-mono${lineCursor === i ? ' active' : ''}`}
                onClick={() => {
                  chessviewerRef.current?.exploreLine(bestLine.afterFen, bestLine.uci, i);
                  setLineCursor(i);
                }}
                title="Play this line on the board"
              >
                {ply.san}
              </button>
            </React.Fragment>
          ))}
          {lineCursor != null ? (
            <button
              type="button"
              className="cm-correct-exit"
              onClick={() => {
                chessviewerRef.current?.exploreLine(bestLine.afterFen, bestLine.uci, -1);
                setLineCursor(null);
              }}
              title="Exit, back to your move"
            >
              <FontAwesomeIcon icon={faXmark} className="me-1" />
              Exit
            </button>
          ) : null}
        </div>
      ) : null}

      {/* The game's own move, with its grade and swing. */}
      <p className="cm-correct-result-detail">
        Best here was{' '}
        <strong>
          {outcome.bestMoveSan ?? uciToSan(selected.Fen, selected.Solution) ?? selected.Solution}
        </strong>
        . You played <strong className="cm-mono">{selected.PlayedMove}</strong>{' '}
        <span className={`cm-correct-detail-grade ${moveTypeClass(selected.MoveType)}`}>
          {MoveType[selected.MoveType]}
          {selected.PlayedSwing > 0 ? (
            <>
              {' '}
              <FontAwesomeIcon icon={faArrowDown} className="me-1" />
              {selected.PlayedSwing}%
            </>
          ) : null}
        </span>
      </p>

      {selected.Commentary ? (
        <p className="cm-correct-result-note text-muted">{selected.Commentary}</p>
      ) : null}

      {outcome.correct && !isAuthenticated ? (
        <div className="cm-correct-nudge">
          <span>Sign up to save your progress and take every game to 100%.</span>
          <div className="cm-correct-nudge-actions">
            <Button size="sm" variant="primary" onClick={openLogin}>
              Sign up
            </Button>
            <button
              type="button"
              className="cm-correct-nudge-skip"
              onClick={hasNextRepair ? advance : stop}
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// "Black to move. You made a blunder", with the classification in its own icon and colour.
function MadeMistakeLine(props: {
  sideToMove: 'white' | 'black';
  moveType: MoveType;
  // The mistake's swing and what repairing it pays now, shown before the attempt. The payout already
  // includes the repair ladder and the hint; `hintUsed` only marks it as discounted.
  swing?: number;
  payoutCents?: number;
  hintUsed?: boolean;
}): React.ReactNode {
  const Icon = getIcon(props.moveType);
  const name = MoveType[props.moveType];
  const article = /^[aeiou]/i.test(name) ? 'an' : 'a';
  const swing = props.swing ?? 0;
  return (
    <div className="cm-correct-prompt cm-practice-prompt">
      <strong>{props.sideToMove === 'white' ? 'White' : 'Black'} to move.</strong> You made{' '}
      {article}{' '}
      <span
        className={`cm-correct-detail-grade cm-correct-prompt-grade ${moveTypeClass(props.moveType)}`}
      >
        <Icon className="cm-cp-move-icon" />
        {name.toLowerCase()}
      </span>
      {swing > 0 ? (
        <span className="cm-prompt-worth">
          <span className={`cm-prompt-swing ${moveTypeClass(props.moveType)}`}>
            &darr;{swing}%
          </span>
          {props.payoutCents ? (
            <span
              className={`cm-prompt-pay${props.hintUsed ? ' is-halved' : ''}`}
              title={props.hintUsed ? 'Halved because you took the hint' : undefined}
            >
              {formatMoney(props.payoutCents)}
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

// The move-class chip (icon + name), as in the game overlay's move header.
function MoveTypeReadout(props: { moveType: MoveType }): React.ReactNode {
  const Icon = getIcon(props.moveType);
  return (
    <span className={`cm-cp-move-type ${moveTypeClass(props.moveType)}`}>
      <Icon className="cm-cp-move-icon" />
      {MoveType[props.moveType]}
    </span>
  );
}
